import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Helmet } from 'react-helmet-async';
import { useSchema } from '@/hooks/useSchema';
import { generateLocalBusinessSchema, generateBreadcrumbSchema, generateFAQSchema } from '@/utils/schema';
import LocationContentLoader from '@/components/Location/LocationContentLoader';
import AutoSEO from '@/components/SEO/AutoSEO';
import IntelligentInternalLinks from '@/components/SEO/IntelligentInternalLinks';
import { 
  MapPin, 
  Shield, 
  Phone, 
  Clock,
  CheckCircle,
  Star,
  Award,
  Users,
  Home,
  Building,
  Zap,
  Umbrella,
  ThumbsUp,
  Calendar
} from 'lucide-react';

interface LocationTemplateProps {
  // These would be populated by your variable system
  cityName: string;
  stateName: string;
  countyName: string;
  zipCodes: string[];
  latitude: number;
  longitude: number;
  population?: number;
  historicalFact?: string;
  nearbyTowns: string[];
}

const LocationTemplate: React.FC<LocationTemplateProps> = ({
  cityName = "{{location.city_name}}",
  stateName = "{{location.state_name}}",
  countyName = "{{location.county_name}}",
  zipCodes = ["{{location.zip_codes}}"],
  latitude = 35.9382,
  longitude = -77.7905,
  population = "{{location.population}}",
  historicalFact = "{{location.historical_fact}}",
  nearbyTowns = ["{{location.nearby_towns}}"]
}) => {

  const fullLocationName = `${cityName}, ${stateName}`;
  const primaryPhone = "(252) 371-2266";
  
  // Advanced conversion psychology: Create urgency and social proof
  const urgencyTriggers = [
    "Voted Best Roofer Near Me - NC",
    "Free Emergency Response",
    "Same-Day Estimates Available", 
    "Limited Time: No Money Down"
  ];

  const socialProofElements = [
    "Best Roofer Near Me - National Roofing Directory",
    "5,000+ Local Homeowners Trust Us",
    "15+ Years Serving Eastern NC", 
    "5-Star Rating Across All Platforms"
  ];

  const handleEmergencyCall = () => {
    window.location.href = `tel:${primaryPhone}`;
  };

  const handleGetQuote = () => {
    window.open('https://carolinatrophyroofs.sky-quote.com/schedule#step1', '_blank');
  };

  // Schema markup for local business
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "RoofingContractor", 
    "@id": `https://www.carolinatrophyroofs.com/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}-${stateName.toLowerCase()}`,
    "name": `Carolina Trophy Roofs - ${fullLocationName}`,
    "url": "https://www.carolinatrophyroofs.com",
    "telephone": primaryPhone,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityName,
      "addressRegion": stateName,
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": latitude,
      "longitude": longitude
    },
    "areaServed": {
      "@type": "City",
      "name": fullLocationName
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates", 
        "latitude": latitude,
        "longitude": longitude
      },
      "geoRadius": "30000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Roofing Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Residential Roof Replacement" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Commercial Roofing" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Emergency Storm Damage Repair" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Roof Inspections" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Insurance Claim Assistance" }}
      ]
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "19:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday", 
        "opens": "08:00",
        "closes": "17:00"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "250",
      "bestRating": "5"
    },
    "priceRange": "$$"
  };

  const services = [
    {
      icon: Home,
      title: "Residential Roofing", 
      description: `Complete residential roofing services for ${cityName} homeowners including replacement, repair, and maintenance.`,
      cta: "Protect Your Home"
    },
    {
      icon: Building,
      title: "Commercial Roofing",
      description: `Professional commercial roofing solutions for ${cityName} businesses with minimal disruption to operations.`,
      cta: "Secure Your Business"
    },
    {
      icon: Zap,
      title: "Emergency Storm Damage",
      description: `24/7 emergency response for storm damage in ${cityName}. Don't let small problems become big disasters.`,
      cta: "Get Emergency Help"
    },
    {
      icon: Umbrella,
      title: "Insurance Claims",
      description: `Complete insurance claim assistance for ${cityName} residents. We handle the paperwork, you get results.`,
      cta: "Maximize Your Claim"
    }
  ];

  const competitiveAdvantages = [
    {
      icon: Shield,
      title: "Fully Insured",
      description: `Comprehensive insurance coverage serving ${cityName} with complete protection for your property.`,
      psychology: "Risk Reversal"
    },
    {
      icon: Award, 
      title: "Local Expertise",
      description: `Deep knowledge of ${cityName} building codes, weather patterns, and permit requirements.`,
      psychology: "Authority & Expertise"
    },
    {
      icon: Users,
      title: "Community Trust",
      description: `Hundreds of satisfied customers throughout ${cityName} and ${countyName} County.`,
      psychology: "Social Proof"
    },
    {
      icon: Clock,
      title: "Rapid Response",
      description: `Fast response times for ${cityName} emergency calls - often same day service available.`,
      psychology: "Urgency & Availability"
    }
  ];

  const conversionTriggers = [
    {
      trigger: "Scarcity",
      message: `Only 3 roofing crews available for ${cityName} this month`,
      action: "Schedule today to secure your spot" 
    },
    {
      trigger: "Social Proof",
      message: `Join 5,000+ homeowners in ${countyName} County who chose Carolina Trophy Roofs`,
      action: "See why your neighbors trust us"
    },
    {
      trigger: "Risk Reversal", 
      message: "100% Satisfaction Guarantee + Lifetime Warranty Options",
      action: "Get your risk-free estimate"
    },
    {
      trigger: "Authority",
      message: "A+ BBB Rating & Better Business Bureau Accredited",
      action: "Work with the trusted experts"
    }
  ];
  const nearbyServiceAreas = nearbyTowns.slice(0, 8);

  // Comprehensive nearby cities database organized by geographic proximity and service relevance
  const getAllNearbyCities = () => {
    const allCities = [
      // Major Eastern NC Cities (Primary Service Hubs)
      { name: 'Rocky Mount', path: '/locations/rocky-mount', county: 'Nash/Edgecombe', priority: 'high' },
      { name: 'Greenville', path: '/locations/greenville', county: 'Pitt', priority: 'high' },
      { name: 'Wilson', path: '/locations/wilson', county: 'Wilson', priority: 'high' },
      { name: 'Raleigh', path: '/locations/raleigh', county: 'Wake', priority: 'high' },
      { name: 'Durham', path: '/locations/durham', county: 'Durham', priority: 'high' },
      { name: 'Tarboro', path: '/locations/tarboro', county: 'Edgecombe', priority: 'high' },
      
      // Triangle Area Cities
      { name: 'Cary', path: '/locations/cary', county: 'Wake', priority: 'medium' },
      { name: 'Apex', path: '/locations/apex', county: 'Wake', priority: 'medium' },
      { name: 'Chapel Hill', path: '/locations/chapel-hill', county: 'Orange', priority: 'medium' },
      { name: 'Clayton', path: '/locations/clayton', county: 'Johnston', priority: 'medium' },
      { name: 'Garner', path: '/locations/garner', county: 'Wake', priority: 'medium' },
      { name: 'Knightdale', path: '/locations/knightdale', county: 'Wake', priority: 'medium' },
      { name: 'Wake Forest', path: '/locations/wake-forest', county: 'Wake', priority: 'medium' },
      { name: 'Morrisville', path: '/locations/morrisville', county: 'Wake', priority: 'medium' },
      
      // Eastern NC Regional Centers
      { name: 'New Bern', path: '/locations/new-bern', county: 'Craven', priority: 'medium' },
      { name: 'Jacksonville', path: '/locations/jacksonville', county: 'Onslow', priority: 'medium' },
      { name: 'Wilmington', path: '/locations/wilmington', county: 'New Hanover', priority: 'medium' },
      { name: 'Elizabeth City', path: '/locations/elizabeth-city', county: 'Pasquotank', priority: 'medium' },
      { name: 'Goldsboro', path: '/locations/goldsboro', county: 'Wayne', priority: 'medium' },
      { name: 'Kinston', path: '/locations/kinston', county: 'Lenoir', priority: 'medium' },
      
      // Coastal Communities
      { name: 'Nags Head', path: '/locations/nags-head', county: 'Dare', priority: 'medium' },
      { name: 'Kill Devil Hills', path: '/locations/kill-devil-hills', county: 'Dare', priority: 'medium' },
      { name: 'Kitty Hawk', path: '/locations/kitty-hawk', county: 'Dare', priority: 'medium' },
      { name: 'Manteo', path: '/locations/manteo', county: 'Dare', priority: 'medium' },
      { name: 'Hatteras', path: '/locations/hatteras', county: 'Dare', priority: 'medium' },
      { name: 'Morehead City', path: '/locations/morehead-city', county: 'Carteret', priority: 'medium' },
      
      // Small Towns & Rural Communities
      { name: 'Nashville', path: '/locations/nashville', county: 'Nash', priority: 'standard' },
      { name: 'Henderson', path: '/locations/henderson', county: 'Vance', priority: 'standard' },
      { name: 'Oxford', path: '/locations/oxford', county: 'Granville', priority: 'standard' },
      { name: 'Louisburg', path: '/locations/louisburg', county: 'Franklin', priority: 'standard' },
      { name: 'Smithfield', path: '/locations/smithfield', county: 'Johnston', priority: 'standard' },
      { name: 'Selma', path: '/locations/selma', county: 'Johnston', priority: 'standard' },
      { name: 'Four Oaks', path: '/locations/four-oaks', county: 'Johnston', priority: 'standard' },
      { name: 'Benson', path: '/locations/benson', county: 'Johnston', priority: 'standard' },
      { name: 'Dunn', path: '/locations/dunn', county: 'Harnett', priority: 'standard' },
      { name: 'Lillington', path: '/locations/lillington', county: 'Harnett', priority: 'standard' },
      { name: 'Fuquay-Varina', path: '/locations/fuquay-varina', county: 'Wake', priority: 'standard' },
      { name: 'Holly Springs', path: '/locations/holly-spring', county: 'Wake', priority: 'standard' },
      { name: 'Angier', path: '/locations/angier', county: 'Harnett', priority: 'standard' },
      { name: 'Coats', path: '/locations/coats', county: 'Harnett', priority: 'standard' },
      { name: 'Kenly', path: '/locations/kenly', county: 'Johnston', priority: 'standard' },
      { name: 'Zebulon', path: '/locations/zebulon', county: 'Wake', priority: 'standard' },
      { name: 'Wendell', path: '/locations/wendell', county: 'Wake', priority: 'standard' },
      { name: 'Rolesville', path: '/locations/rolesville', county: 'Wake', priority: 'standard' },
      { name: 'Youngsville', path: '/locations/youngsville', county: 'Franklin', priority: 'standard' },
      { name: 'Franklinton', path: '/locations/franklinton', county: 'Franklin', priority: 'standard' },
      { name: 'Creedmoor', path: '/locations/creedmoor', county: 'Granville', priority: 'standard' },
      { name: 'Butner', path: '/locations/butner', county: 'Granville', priority: 'standard' },
      { name: 'Stem', path: '/locations/stem', county: 'Granville', priority: 'standard' }
    ];

    // Filter out current city and return nearby cities based on intelligent logic
    return allCities
      .filter(city => city.name.toLowerCase() !== cityName.toLowerCase())
      .sort((a, b) => {
        // Prioritize by relevance: high > medium > standard
        const priorityOrder = { 'high': 3, 'medium': 2, 'standard': 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        // Then alphabetically within same priority
        return a.name.localeCompare(b.name);
      });
  };

  const nearbyCities = getAllNearbyCities();
  const primaryNearbyCities = nearbyCities.filter(city => city.priority === 'high').slice(0, 6);
  const secondaryNearbyCities = nearbyCities.filter(city => city.priority === 'medium').slice(0, 8);
  const additionalCities = nearbyCities.filter(city => city.priority === 'standard').slice(0, 12);

  // Generate FAQ data for schema
  const locationFAQs = [
    {
      question: `What makes Carolina Trophy Roofs the best roofer near me in ${cityName}?`,
      answer: `Carolina Trophy Roofs is voted #1 in the National Roofing Directory for North Carolina. We serve ${cityName} with 15+ years of experience, full insurance coverage, A+ BBB rating, and over 5,000 satisfied customers throughout ${countyName} County.`
    },
    {
      question: `Do you offer emergency roofing services in ${cityName}?`,
      answer: `Yes, we provide 24/7 emergency roofing services throughout ${cityName} and ${countyName} County. Our rapid response team can often provide same-day service for urgent roof repairs and storm damage.`
    },
    {
      question: `How much does roof replacement cost in ${cityName}, NC?`,
      answer: `Roof replacement costs in ${cityName} vary based on materials, size, and complexity. We offer free estimates and work with all insurance companies to maximize your coverage. Contact us for a personalized quote.`
    },
    {
      question: `Are you licensed and insured to work in ${cityName}?`,
      answer: `Yes, Carolina Trophy Roofs is fully licensed and insured to work throughout ${cityName} and all of ${countyName} County. We carry comprehensive liability insurance and workers' compensation for complete protection.`
    }
  ];

  // Generate breadcrumbs for schema
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.carolinatrophyroofs.com' },
    { name: 'Service Areas', url: 'https://www.carolinatrophyroofs.com/locations' },
    { name: `${cityName}, ${stateName}`, url: `https://www.carolinatrophyroofs.com/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}` }
  ];

  // Use comprehensive schema markup
  useSchema({
    type: 'multiple',
    schemas: [
      generateLocalBusinessSchema(cityName, latitude, longitude, zipCodes),
      generateBreadcrumbSchema(breadcrumbs),
      generateFAQSchema(locationFAQs)
    ]
  });

  const highDAReferences = [
    { name: 'National Roofing Directory - North Carolina', url: 'https://www.nationalroofingdirectory.com/north-carolina' },
    { name: 'Better Business Bureau - Roofing Contractors', url: 'https://www.bbb.org/article/tips/13747-bbb-tip-hiring-a-roofing-contractor' },
    { name: 'National Roofing Contractors Association', url: 'https://www.nrca.net/homeowner-resources' },
    { name: 'International Association of Certified Home Inspectors', url: 'https://www.nachi.org/roof-inspection.htm' },
    { name: 'Consumer Reports - Roofing Guide', url: 'https://www.consumerreports.org/home-garden/roofing/' },
    { name: 'NC Department of Insurance - Consumer Resources', url: 'https://www.ncdoi.gov/consumers' },
    { name: 'Federal Trade Commission - Hiring Contractors', url: 'https://www.consumer.ftc.gov/articles/hiring-contractor' },
    { name: 'North Carolina Attorney General - Consumer Protection', url: 'https://ncdoj.gov/protecting-consumers/' }
  ];

  return (
    <Layout>
      <AutoSEO
        pageType="location"
        locationData={{
          cityName,
          stateName,
          latitude,
          longitude
        }}
      />
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Best Roofer Near Me in {fullLocationName} | Carolina Trophy Roofs</title>
        <meta name="description" content={`Best roofer near me in ${fullLocationName} - Voted #1 in National Roofing Directory for North Carolina. Fully insured with 15+ years experience serving ${countyName} County. Free estimates, 24/7 emergency service.`} />
        <meta name="keywords" content={`best roofer near me ${cityName}, roofing contractor ${cityName}, roof repair ${cityName}, roof replacement ${cityName} NC, storm damage ${cityName}, residential roofing ${countyName} county`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`Best Roofer Near Me in ${fullLocationName} | Carolina Trophy Roofs`} />
        <meta property="og:description" content={`Best roofer near me in ${fullLocationName} - Voted #1 in National Roofing Directory for North Carolina. Fully insured and locally owned with 15+ years experience.`} />
        <meta property="og:url" content={`https://www.carolinatrophyroofs.com/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}-nc`} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={`Best Roofer Near Me in ${fullLocationName}`} />
        <meta property="twitter:description" content={`Best roofer near me in ${fullLocationName} - Voted #1 in National Roofing Directory for NC. Free estimates, 24/7 emergency service.`} />
        
        {/* Local SEO Meta Tags */}
        <meta name="geo.region" content={stateName} />
        <meta name="geo.placename" content={cityName} />
        <meta name="geo.position" content={`${latitude};${longitude}`} />
        <meta name="ICBM" content={`${latitude}, ${longitude}`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://www.carolinatrophyroofs.com/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}-nc`} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Hero Section with Advanced Psychology */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-charcoal-gray to-gray-800 text-white">
          <div className="max-w-7xl mx-auto">
            
            {/* Urgency Badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {urgencyTriggers.map((trigger, index) => (
                <Badge key={index} variant="outline" className="border-yellow-400 text-yellow-400 px-3 py-1">
                  <Zap className="w-3 h-3 mr-1" />
                  {trigger}
                </Badge>
              ))}
            </div>

            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Best Roofer Near Me
                <span className="text-brand-trophy-gold block mt-2">{fullLocationName}</span>
              </h1>
              
              <div className="mb-6">
                <Badge variant="outline" className="border-yellow-400 text-yellow-400 px-6 py-2 text-lg font-bold">
                  <Award className="w-5 h-5 mr-2" />
                  Voted #1 in National Roofing Directory for North Carolina
                </Badge>
              </div>
              
              <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
                When {cityName} residents search "best roofer near me," they choose Carolina Trophy Roofs. 
                Serving {cityName} homeowners and businesses with premium roofing services since 2008. 
                Fully insured and locally owned with over 5,000 satisfied customers in North Carolina.
                {historicalFact && ` ${historicalFact}`}
              </p>

              {/* Social Proof Elements */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {socialProofElements.map((proof, index) => (
                  <div key={index} className="flex items-center text-yellow-400">
                    <Star className="w-4 h-4 mr-2 fill-current" />
                    <span className="text-sm font-medium">{proof}</span>
                  </div>
                ))}
              </div>

              {/* Primary CTA with Psychology */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button 
                  onClick={handleGetQuote}
                  size="lg"
                  className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-4 text-lg"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Get FREE Estimate Today
                </Button>
                <Button 
                  onClick={handleEmergencyCall}
                  variant="outline"
                  size="lg"
                  className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-4"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now: {primaryPhone}
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center max-w-2xl mx-auto">
                <div>
                  <div className="text-2xl font-bold text-brand-trophy-gold">5,000+</div>
                  <div className="text-sm text-gray-300">Local Projects</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-brand-trophy-gold">15+</div>
                  <div className="text-sm text-gray-300">Years Experience</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-brand-trophy-gold">24/7</div>
                  <div className="text-sm text-gray-300">Emergency Service</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-brand-trophy-gold">100%</div>
                  <div className="text-sm text-gray-300">Fully Insured</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section with Conversion Psychology */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Complete Roofing Services in {cityName}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                From emergency repairs to complete roof replacements, we're {cityName}'s most trusted roofing contractor.
                Every job backed by our 100% satisfaction guarantee.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => (
                <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-brand-trophy-gold">
                  <CardHeader>
                    <service.icon className="w-12 h-12 text-brand-trophy-gold mx-auto mb-4" />
                    <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      onClick={handleGetQuote}
                      variant="outline" 
                      className="w-full hover:bg-brand-trophy-gold hover:text-white transition-colors"
                    >
                      {service.cta}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us - Advanced Psychology */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why We're the Best Roofer Near Me in {cityName}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Don't risk your family's safety and your home's value with inexperienced contractors. 
                Here's why we're voted the best roofer near me in {cityName} by the National Roofing Directory.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {competitiveAdvantages.map((advantage, index) => (
                <div key={index} className="text-center">
                  <advantage.icon className="w-16 h-16 text-brand-trophy-gold mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{advantage.title}</h3>
                  <p className="text-gray-600 mb-3">{advantage.description}</p>
                  <Badge variant="secondary" className="text-xs">
                    {advantage.psychology}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Conversion Psychology Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {conversionTriggers.map((trigger, index) => (
                <Alert key={index} className="border-2 border-brand-trophy-gold/20 bg-yellow-50">
                  <ThumbsUp className="h-5 w-5 text-brand-trophy-gold" />
                  <AlertDescription className="text-gray-800">
                    <div className="font-semibold mb-2">{trigger.message}</div>
                    <div className="text-sm text-gray-600">{trigger.action}</div>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Proudly Serving {cityName} and Surrounding Areas
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-brand-trophy-gold text-brand-charcoal-gray p-3 rounded-lg font-semibold">
                <MapPin className="w-4 h-4 mx-auto mb-1" />
                {cityName} (Primary)
              </div>
              {nearbyServiceAreas.map((area, index) => (
                <div key={index} className="bg-white p-3 rounded-lg shadow-sm border">
                  <CheckCircle className="w-4 h-4 text-brand-trophy-gold mx-auto mb-1" />
                  <span className="text-sm font-medium text-gray-700">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* High DA References */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Trusted Resources & Industry Authority
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {highDAReferences.map((ref, index) => (
                <a 
                  key={index}
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 bg-white border border-gray-200 rounded-lg hover:border-brand-trophy-gold hover:shadow-md transition-all"
                >
                  <div className="text-sm font-medium text-brand-charcoal-gray hover:text-brand-trophy-gold">
                    {ref.name}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Enhanced Location-Specific Content */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Expert Roofing Services in {cityName}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Local expertise, premium materials, and personalized service for {cityName} homeowners and businesses.
              </p>
            </div>
            <LocationContentLoader 
              cityName={cityName} 
              countyName={countyName}
              population={typeof population === 'number' ? population : 0}
              longitude={longitude}
            />
          </div>
        </section>

        {/* Nearby Cities Network - Strategic Internal Linking */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white to-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Also Serving Communities Near {cityName}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Carolina Trophy Roofs is proud to serve {cityName} and surrounding Eastern North Carolina communities. 
                Click below to learn about our services in your specific area.
              </p>
            </div>

            {/* Primary Service Areas (Major Cities) */}
            {primaryNearbyCities.length > 0 && (
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                  Major Service Areas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {primaryNearbyCities.map((city, index) => (
                    <Link
                      key={index}
                      to={city.path}
                      className="group bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray rounded-lg p-4 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    >
                      <MapPin className="w-5 h-5 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <div className="font-bold text-sm leading-tight">{city.name}</div>
                      <div className="text-xs opacity-80 mt-1">{city.county}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Secondary Service Areas (Regional Centers) */}
            {secondaryNearbyCities.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-gray-700 mb-6 text-center">
                  Regional Communities We Serve
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                  {secondaryNearbyCities.map((city, index) => (
                    <Link
                      key={index}
                      to={city.path}
                      className="group bg-white hover:bg-gray-50 border border-gray-200 hover:border-brand-trophy-gold rounded-lg p-3 text-center transition-all duration-300 hover:shadow-md"
                    >
                      <CheckCircle className="w-4 h-4 text-brand-trophy-gold mx-auto mb-1 group-hover:scale-110 transition-transform" />
                      <div className="font-medium text-sm text-gray-800 group-hover:text-brand-trophy-gold leading-tight">
                        {city.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{city.county}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Communities */}
            {additionalCities.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-600 mb-4 text-center">
                  Additional Communities in Our Service Area
                </h3>
                <div className="flex flex-wrap justify-center gap-2">
                  {additionalCities.map((city, index) => (
                    <Link
                      key={index}
                      to={city.path}
                      className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-brand-trophy-gold/10 border border-gray-200 hover:border-brand-trophy-gold rounded-full text-sm text-gray-700 hover:text-brand-trophy-gold transition-all duration-200 hover:scale-105"
                    >
                      <span className="mr-1">📍</span>
                      {city.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Service Area Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Comprehensive Eastern NC Coverage
              </h3>
              <p className="text-blue-700 text-sm leading-relaxed">
                Don't see your community listed? We serve over 200 locations across Eastern North Carolina. 
                <Link to="/service-areas" className="font-semibold hover:underline ml-1">
                  View our complete service area map
                </Link> 
                or call <strong>(252) 371-2266</strong> to confirm we serve your location.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA with Maximum Psychology */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-brand-charcoal-gray text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold mb-4">
              Don't Wait Until It's Too Late
            </h3>
            <p className="text-xl text-gray-300 mb-6">
              Every day you wait, small roofing problems become expensive disasters. 
              Join hundreds of smart {cityName} homeowners who chose proactive protection.
            </p>
            
            <Alert className="max-w-2xl mx-auto mb-8 bg-red-900/20 border-red-400">
              <Zap className="h-5 w-5 text-red-400" />
              <AlertDescription className="text-red-100">
                <strong>WARNING:</strong> Storm season is approaching. Don't risk your family's safety with a damaged roof. 
                Call now for priority scheduling.
              </AlertDescription>
            </Alert>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleGetQuote}
                size="lg"
                className="bg-brand-trophy-gold hover:bg-brand-trophy-gold/90 text-brand-charcoal-gray font-bold px-8 py-4 text-lg"
              >
                <Calendar className="w-6 h-6 mr-2" />
                Schedule FREE Inspection
              </Button>
              <Button 
                onClick={handleEmergencyCall}
                variant="outline"
                size="lg"
                className="border-white text-black hover:bg-white hover:text-brand-charcoal-gray font-semibold px-8 py-4"
              >
                <Phone className="w-6 h-6 mr-2" />
                Emergency: {primaryPhone}
              </Button>
            </div>
          </div>
        </section>

        {/* Intelligent Internal Links Section */}
        <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <IntelligentInternalLinks
              currentPage={`/locations/${cityName.toLowerCase().replace(/\s+/g, '-')}`}
              content={`${cityName} roofing services residential commercial storm damage repair roof replacement installation`}
              pageType="location"
              keywords={[cityName, `${cityName} roofing`, 'residential roofing', 'commercial roofing']}
              variant="footer"
              maxLinks={6}
            />
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default LocationTemplate;